<script>
	import { repage } from "cando-util";
	import { doc, getFirestore, setDoc } from "firebase/firestore";
	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import {
		faArrowDown,
		faArrowUp,
		faXmark,
		faRotateLeft,
		faSave,
	} from "@fortawesome/free-solid-svg-icons";

	import { store } from "$lib/utilities/store";
	import { checkCriteria } from "$lib/utilities/checkCriteria";
	import { makeStringMatcher } from "$lib/utilities/makeStringMatcher";
	import { tableSort, sortField } from "$lib/utilities/tableSort";
	import { gotoHref } from "$lib/utilities/handleNav";
	import { getLangHref } from "$lib/utilities/getLangHref";

	import {
		buildAutoTableFields,
		setDefault,
		getDefault,
	} from "./tableColumnsUtil.js";

	import ScreenSize from "$lib/components/ScreenSize.svelte";
	import Search from "$lib/components/Search.svelte";
	import AddItem from "./AddItem.svelte";
	import RecordCount from "$lib/components/RecordCount.svelte";
	import Paging from "$lib/components/Paging.svelte";
	import TableField from "./TableField.svelte";

	export let col;
	export let lang;
	export let area;
	export let requiredFields;
	export let tableFields = [];
	export let tableDefaultsSettingId = "App Settings";
	export let action = async (docId) => {
		await gotoHref(
			getLangHref(
				`/admin/?col=${encodeURIComponent(col)}&docId=${encodeURIComponent(docId)}`,
				lang,
				area,
			),
			true,
		);
	};
	export let includeAll = true; // if false, this is going to exclude documents that were created without translations (old method)
	export let altDocs;
	export let autoGenId = false;
	export let addDisabled = false;
	export let adjustLeftMargin = true;
	export let allowEdit = true;
	export let showSearch = true;
	export let allowTableColumnEdit = true;

	let dragHeading = null;
	let dragging = false;
	let localColumnOrder = [];
	let providedTableFields = [];
	let autoGeneratedFields = [];
	let baseTableFields = [];
	let effectiveColumnOrder = [];
	let resolvedTableFields = [];

	$: docs = altDocs || $store?.[col]?.docs || {};

	$: criteria = $store.userProfile?.collectionCriteria?.[col];

	$: tableSortFields = criteria?.tableSortFields || [];

	$: checkCriteria(
		col,
		criteria,
		$store?.userProfile?.uid,
		$store,
		tableDefaultsSettingId,
	);

	$: sortedItemIds = Object.keys(docs)
		.filter(
			(docId) =>
				includeAll ||
				Object.keys(docs[docId]).filter(
					(key) => typeof docs[docId][key] === "string",
				).length === 0,
		)
		.sort((a, b) => tableSort(a, b, tableSortFields, resolvedTableFields));

	$: filterItemIds = sortedItemIds.filter((did) => {
		const searchString =
			$store?.userProfile?.collectionCriteria?.[col]?.search;
		if (!searchString) return true;
		const str =
			JSON.stringify(
				resolvedTableFields
					.filter((f) => f.function)
					.map((f) => f.function(did)),
			) + (did || "").toString();
		const stringMatcher = makeStringMatcher(searchString || "");
		return stringMatcher(str.toLowerCase());
	});

	$: collectionStore = {
		docs: sortedItemIds.reduce(
			(a, sid) => ({ ...a, [sid]: docs[sid] }),
			{},
		),
		pages: repage(
			filterItemIds,
			$store.userProfile?.collectionCriteria?.[col]?.pageSize ?? 5,
		),
	};

	const handleSortField = async (e) => {
		if (dragging) return;
		await sortField(
			e,
			$store?.userProfile?.uid,
			col,
			resolvedTableFields,
			tableSortFields,
		);
	};

	$: providedTableFields = Array.isArray(tableFields) ? tableFields : [];
	$: autoGeneratedFields = providedTableFields.length
		? []
		: buildAutoTableFields(docs, col);
	$: baseTableFields = providedTableFields.length
		? providedTableFields
		: autoGeneratedFields;

	$: effectiveColumnOrder = localColumnOrder.length
		? localColumnOrder
		: criteria?.columnOrder || [];

	$: if (
		localColumnOrder.length &&
		criteria?.columnOrder &&
		localColumnOrder.length === criteria.columnOrder.length &&
		localColumnOrder.every(
			(heading, idx) => heading === criteria.columnOrder[idx],
		)
	) {
		localColumnOrder = [];
	}
	$: resolvedTableFields = applyColumnOrder(
		baseTableFields,
		effectiveColumnOrder,
	);

	$: allHeadings = baseTableFields.map((f) => f.heading);

	$: selectedHeadingsBase =
		effectiveColumnOrder && effectiveColumnOrder.length
			? normalizeHeadingOrder(baseTableFields, effectiveColumnOrder)
			: [];

	$: selectedHeadings = (() => {
		const docIdHeading = baseTableFields.find(
			(f) => f.isIdentifier || f.key === "__docId",
		)?.heading;
		let s = selectedHeadingsBase.slice();
		if (docIdHeading) {
			s = s.filter((h) => h !== docIdHeading);
			s.unshift(docIdHeading);
		}
		return s;
	})();

	$: hiddenHeadings = allHeadings.filter(
		(h) => !selectedHeadings.includes(h),
	);

	const removeColumn = async (heading) => {
		const docIdHeading = baseTableFields.find(
			(f) => f.isIdentifier || f.key === "__docId",
		)?.heading;
		if (!heading || heading === docIdHeading) return;
		const currentOrder =
			effectiveColumnOrder && effectiveColumnOrder.length
				? effectiveColumnOrder.slice()
				: docIdHeading
					? [docIdHeading]
					: [];
		const newOrder = currentOrder.filter((h) => !!h && h !== heading);
		localColumnOrder = newOrder;
		await persistColumnOrder(newOrder);
	};

	const addColumn = async (heading) => {
		if (!heading) return;
		const docIdHeading = baseTableFields.find(
			(f) => f.isIdentifier || f.key === "__docId",
		)?.heading;
		let currentOrder =
			effectiveColumnOrder && effectiveColumnOrder.length
				? effectiveColumnOrder.slice()
				: docIdHeading
					? [docIdHeading]
					: [];
		if (!currentOrder.includes(heading)) currentOrder.push(heading);
		// ensure doc id is first
		currentOrder = currentOrder.filter((h) => !!h && h !== docIdHeading);
		if (docIdHeading) currentOrder.unshift(docIdHeading);
		localColumnOrder = currentOrder;
		await persistColumnOrder(currentOrder);
	};
	let screenWidth;
	let breakpoint;

	const normalizeHeadingOrder = (fields, order = []) => {
		const availableHeadings = fields.map((field) => field.heading);
		return (order || []).filter((heading) =>
			availableHeadings.includes(heading),
		);
	};

	const applyColumnOrder = (fields, order = []) => {
		const allHeadings = fields.map((f) => f.heading);
		const docIdHeading = fields.find(
			(f) => f.isIdentifier || f.key === "__docId",
		)?.heading;

		let selectedHeadings =
			order && order.length
				? normalizeHeadingOrder(fields, order)
				: docIdHeading
					? [docIdHeading]
					: [];

		// Ensure doc id is always first
		if (docIdHeading) {
			selectedHeadings = selectedHeadings.filter(
				(h) => h !== docIdHeading,
			);
			selectedHeadings.unshift(docIdHeading);
		}

		return selectedHeadings
			.map((heading) => fields.find((field) => field.heading === heading))
			.filter(Boolean);
	};

	const moveHeading = (order, fromIndex, toIndex) => {
		const result = [...order];
		const [item] = result.splice(fromIndex, 1);
		result.splice(toIndex, 0, item);
		return result;
	};

	const persistColumnOrder = async (newOrder) => {
		if (typeof $store?.userProfile?.uid !== "string") return;
		const mergedCriteria = {
			...($store.userProfile?.collectionCriteria?.[col] || {}),
			columnOrder: newOrder,
		};
		store.set({
			...$store,
			userProfile: {
				...$store.userProfile,
				collectionCriteria: {
					...($store.userProfile?.collectionCriteria || {}),
					[col]: mergedCriteria,
				},
			},
		});
		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);
		const updates = { collectionCriteria: { [col]: mergedCriteria } };
		console.log({ updates });
		await setDoc(profileDocRef, updates, { merge: true });
	};

	const handleDropOnHeading = async (targetHeading) => {
		if (!dragHeading || dragHeading === targetHeading) {
			handleDragEnd();
			return;
		}
		const baseFields = baseTableFields;
		const normalizedOrder = normalizeHeadingOrder(
			baseFields,
			effectiveColumnOrder,
		);
		const fromIndex = normalizedOrder.indexOf(dragHeading);
		const toIndex = normalizedOrder.indexOf(targetHeading);
		if (fromIndex === -1 || toIndex === -1) {
			handleDragEnd();
			return;
		}
		const updatedOrder = moveHeading(normalizedOrder, fromIndex, toIndex);
		localColumnOrder = updatedOrder;
		try {
			await persistColumnOrder(updatedOrder);
		} finally {
			handleDragEnd();
		}
	};

	const handleDragStart = (heading) => {
		dragHeading = heading;
		dragging = true;
	};

	const handleDragEnd = () => {
		dragHeading = null;
		dragging = false;
	};

	$: leftMargin =
		adjustLeftMargin && screenWidth > 1440 ? (screenWidth - 1440) / 2 : 0;

	const handleSetDefault = async () => {
		await setDefault($store, col, tableDefaultsSettingId);
	};

	const handleGetDefault = async () => {
		await getDefault($store, col, tableDefaultsSettingId);
	};

	$: defaultTableLayouts =
		$store.settings?.docs?.[tableDefaultsSettingId]?.defaultTableLayouts
			?.value || "";

	$: if (allowTableColumnEdit === false && !!defaultTableLayouts) {
		getDefault($store, col, tableDefaultsSettingId);
	}
</script>

<ScreenSize bind:width={screenWidth} bind:breakpoint showBreakpoint={false} />

<div class={`max-w-360 px-2 sm:px-4`} style={`margin-left: ${leftMargin}px;`}>
	<!-- Table -->
	<header class="my-2 shadow-xl sm:my-4">
		<div class="flex justify-between">
			<div class="flex-1 gap-2 sm:flex sm:gap-4">
				{#if showSearch}
					<Search collection={col} />
				{/if}
			</div>
			{#if requiredFields}
				<AddItem
					{col}
					{lang}
					{requiredFields}
					{autoGenId}
					disabled={addDisabled}
				/>
			{/if}
		</div>
		<div class="flex justify-between items-center">
			<div class="w-full">
				<RecordCount
					count={filterItemIds.length}
					total={sortedItemIds.length}
					collection={col}
					status={$store[col]?.status}
				/>
			</div>
			<!-- dropdown for hidden columns -->
			<div class="flex gap-2">
				{#if hiddenHeadings && hiddenHeadings.length > 0 && allowTableColumnEdit}
					<select
						class="select select-sm w-64"
						on:change={(e) => {
							const v = e.target.value;
							if (v) addColumn(v);
							e.target.value = "";
						}}
					>
						<option value="">+ Add column...</option>
						{#each hiddenHeadings as h}
							<option value={h}>{h}</option>
						{/each}
					</select>
				{/if}
				{#if allowTableColumnEdit}
					<button
						class="btn btn-sm hover-ripple"
						type="button"
						on:click={handleGetDefault}
						title="Get Default Columns"
					>
						<SvelteFa icon={faRotateLeft} size="1x" />
					</button>
				{/if}
				{#if $store.userRoles?.admin}
					<button
						class="btn btn-sm gap-2 hover-ripple"
						type="button"
						on:click={handleSetDefault}
						title="Set As Default Columns"
					>
						<SvelteFa icon={faSave} size="1x" />
					</button>
				{/if}
			</div>
		</div>
	</header>
	<div class="my-2 overflow-x-auto shadow-xl sm:my-4">
		<table class="table table-zebra whitespace-nowrap">
			<thead>
				<tr>
					{#each resolvedTableFields.filter((f) => !f.hideInTable) as fld}
						<th
							class={`${fld.function ? "hover:cursor-pointer" : ""}`}
							on:click={handleSortField}
							heading={fld.heading}
							draggable={allowTableColumnEdit}
							on:dragstart={() => handleDragStart(fld.heading)}
							on:dragend={handleDragEnd}
							on:dragover|preventDefault
							on:drop|preventDefault={() =>
								handleDropOnHeading(fld.heading)}
						>
							<div
								class="flex items-center gap-2 justify-between"
							>
								<span class="flex gap-4 items-center">
									{fld.heading}
									{#if tableSortFields.find((f) => f.heading === fld.heading)?.dir === "asc"}
										<SvelteFa
											icon={faArrowUp}
											size="1.5x"
										/>
									{/if}
									{#if tableSortFields.find((f) => f.heading === fld.heading)?.dir === "desc"}
										<SvelteFa
											icon={faArrowDown}
											size="1.5x"
										/>
									{/if}
								</span>
								{#if !fld.isIdentifier && allowTableColumnEdit}
									<button
										on:click|stopPropagation={() =>
											removeColumn(fld.heading)}
										class="ml-2 btn btn-sm btn-ghost"
										aria-label="Remove column"
									>
										<SvelteFa icon={faXmark} size="1.5x" />
									</button>
								{/if}
							</div>
						</th>
					{/each}
				</tr>
			</thead>
			<tbody>
				{#each collectionStore?.pages?.[$store.userProfile?.collectionCriteria?.[col]?.pageIndex] || [] as docId}
					<tr
						class={`${action !== null ? "hover" : ""}`}
						persistSearch="true"
					>
						{#each resolvedTableFields.filter((f) => !f.hideInTable) as fld}
							<TableField
								{fld}
								{col}
								{docId}
								{docs}
								{action}
								{allowEdit}
							/>
						{/each}
					</tr>
				{/each}
			</tbody>
		</table>
	</div>

	{#if criteria}
		<Paging collection={col} totalRecordCount={filterItemIds.length} />
	{/if}
</div>
